import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableToolbar,
  TableToolbarContent,
  Pagination,
  Button,
  InlineLoading,
  Checkbox,
  Popover,
  PopoverContent,
  Dropdown,
  DataTableSkeleton,
} from '@carbon/react';
import PageLayout from '../common/PageLayout';
import { useContext } from 'react';
import { ToastNotificationContext } from '../../content-mui/context/ToastNotificationContext';
import { useHistory } from 'react-router-dom';
import { getDummyGroups } from './mergeReviewConstants';
/* ------------------------------------
   Match color rules (visual contract)
------------------------------------ */
const MATCH_STYLE = {
  EXACT: {
    background: '#E3F6E5',
    color: '#0E6027',
  },
  FUZZY: {
    background: '#FFF4CE',
    color: '#8A6D3B',
  },
};

const MatchReviewGroupDetail = () => {
  const history = useHistory();
  const { setToastNotificationConfig } = useContext(ToastNotificationContext);
  const [openColumnSelector, setOpenColumnSelector] = useState(false);
  const columnPopoverRef = useRef(null);
  const columnTriggerRef = useRef(null);

  const { groupId } = useParams();
  const [isColumnPopoverOpen, setIsColumnPopoverOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState([]);
  const [matchFilter, setMatchFilter] = useState(null); // null | EXACT | FUZZY
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const matchByRowId = useMemo(() => {
    if (!group || !group.records) return {};

    const map = {};
    group.records.forEach(r => {
      map[r.id] = r.match_type;
    });
    return map;
  }, [group]);

  useEffect(() => {
    const handleOutsideClick = event => {
      if (
        !columnPopoverRef.current?.contains(event.target) &&
        !columnTriggerRef.current?.contains(event.target)
      ) {
        setOpenColumnSelector(false);
      }
    };

    if (openColumnSelector) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [openColumnSelector]);

  const selectedColumns = useMemo(() => {
    if (!group) return [];
    return group.all_columns.filter(col => visibleColumns.includes(col));
  }, [group, visibleColumns]);

  const unselectedColumns = useMemo(() => {
    if (!group) return [];
    return group.all_columns.filter(col => !visibleColumns.includes(col));
  }, [group, visibleColumns]);

  const buildGroupDetailFromRecords = groupId => {
    const allRecords = getDummyGroups();

    // 1. Filter records by group
    const groupRecords = allRecords.filter(
      r => String(r.group) === String(groupId)
    );

    if (!groupRecords.length) return null;

    return {
      group_id: groupId,
      entity_type: 'Employee',
      status: localStorage.getItem(`merge_status_${groupId}`) || 'PENDING',
      record_count: groupRecords.length,

      // 2. Columns come from actual data
      all_columns: [
        'first',
        'last',
        'address',
        'city',
        'province',
        'postal',
        'phone',
        'email',
        'account',
        'source_file',
        'file'
      ],

      default_visible_columns: [
        'first',
        'last',
        'address',
        'city',
        'province',
        'postal',
        'phone',
        'email',
        'account',
        'source_file',
        'file'
      ],

      // 3. Normalize records into DataTable-friendly shape
      records: groupRecords.map(r => ({
        id: r.id, // record id
        status: 'PENDING',
        values: {
          first: r.first,
          last: r.last,
          address: r.address,
          city: r.city,
          province: r.province,
          postal: r.postal,
          phone: r.phone,
          email: r.email,
          account: r.account,
          source_file: r.source_file,
          file: r.file || '', // or 'input' / 'history' if you want default
        },
        match_type: {},
      }))
      
    };
  };

  /* ------------------------------------
     Fetch group detail (mocked)
  ------------------------------------ */
  /* ------------------------------------
     Fetch group detail (mocked)
  ------------------------------------ */
  useEffect(() => {
    setLoading(true);

    // Fake a 1-second API call delay
    setTimeout(() => {
      const data = buildGroupDetailFromRecords(groupId);
      if (!data) {
        history.push('/merge-review');
        return;
      }
      setGroup(data);
      setVisibleColumns(data.default_visible_columns);
      setLoading(false);
    }, 1000); // 1000ms = 1 second
  }, [groupId]);

  /* ------------------------------------
     Derived headers
  ------------------------------------ */
  const headers = useMemo(
    () =>
      visibleColumns.map(col => ({
        key: col,
        header: col.replace(/_/g, ' ').toUpperCase(),
      })),
    [visibleColumns]
  );

  /* ------------------------------------
     Reset filters
  ------------------------------------ */
  const resetFilters = () => {
    if (!group) return;
    setVisibleColumns(group.default_visible_columns);
    setMatchFilter(null);
  };

  /* ------------------------------------
     Approve / Reject
  ------------------------------------ */

  const handleApprove = async () => {
    // 🔹 Simulate API update (replace later with real API)

    setGroup(prev => ({
      ...prev,
      status: 'APPROVED',
    }));

    localStorage.setItem(`merge_status_${groupId}`, 'APPROVED');

    setToastNotificationConfig({
      open: true,
      kind: 'success',
      title: 'Merge Approved',
      subtitle: `Match group ${group.group_id} was approved successfully.`,
    });

    // 🔹 Redirect after short delay (better UX)
    history.push('/merge-review');
  };

  const handleReject = async () => {
    setGroup(prev => ({
      ...prev,
      status: 'REJECTED',
    }));
    localStorage.setItem(`merge_status_${groupId}`, 'REJECTED');
    setToastNotificationConfig({
      open: true,
      kind: 'error',
      title: 'Merge Rejected',
      subtitle: `Match group ${group.group_id} was rejected.`,
    });

    history.push('/merge-review');
  };

  const handleBack = () => {
    history.push('/merge-review');
  };

  const NICKNAME_CELL_STYLE = {
    minWidth: '300px',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  };
  return (
    <PageLayout
      pageHeader={`Match Group: ${groupId}`}
      showBackButton={true}
      handleBackButtonClick={handleBack}
      pageDesc="Review matched records and decide whether they should be merged."
      showPrimaryActionButton={false}>
      <div className="tabs-group-content">
        <div className="page-content-wrapper match-review-group-detail">
          {loading || !group ? (
            <DataTableSkeleton columnCount={4} rowCount={4} showHeader={false} />
          ) : (
            <>
              {/* ---------------- Header Actions ---------------- */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}>
                <div>
                  <p>
                    <strong>Status:</strong> Pending
                  </p>
                  <p>
                    <strong>Total Records:</strong> {group.record_count}
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <Button kind="secondary" onClick={handleReject}>
                    Reject
                  </Button>
                  <Button kind="primary" onClick={handleApprove}>
                    Approve
                  </Button>
                </div>
              </div>

              {/* ---------------- Controls ---------------- */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px',
                }}>
                <TableToolbar>
                  <TableToolbarContent
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <div
                      style={{
                        marginLeft: 'auto', // 🔥 THIS IS THE KEY
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}>
                      <Button
                        kind="ghost"
                        size="sm"
                        onClick={() => setOpenColumnSelector(prev => !prev)}
                        ref={columnTriggerRef}>
                        Select Columns
                      </Button>

                      <Popover
                        ref={columnPopoverRef}
                        open={openColumnSelector}
                        align="bottom-right"
                        className="mt-12">
                        <PopoverContent>
                          <div
                            className="checkbox-wrapper"
                            style={{
                              maxHeight: '320px',
                              overflowY: 'auto',
                              minWidth: '260px',
                              padding: '12px 16px',
                            }}>
                            {selectedColumns.map(col => (
                              <div
                                key={col}
                                className="checkbox-border selected">
                                <Checkbox
                                  id={`selected-${col}`}
                                  labelText={col.replace(/_/g, ' ')}
                                  checked
                                  disabled={selectedColumns.length === 1}
                                  onChange={() => {
                                    if (selectedColumns.length === 1) return;
                                    setVisibleColumns(prev =>
                                      prev.filter(c => c !== col)
                                    );
                                  }}
                                />
                              </div>
                            ))}

                            {unselectedColumns.map(col => (
                              <div key={col} className="checkbox-border">
                                <Checkbox
                                  id={`unselected-${col}`}
                                  labelText={col.replace(/_/g, ' ')}
                                  checked={false}
                                  onChange={() =>
                                    setVisibleColumns(prev => [...prev, col])
                                  }
                                />
                              </div>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>

                      <Button kind="ghost" size="sm" onClick={resetFilters}>
                        Reset Filters
                      </Button>
                    </div>
                  </TableToolbarContent>
                </TableToolbar>
              </div>

              {/* ---------------- Table ---------------- */}
              <div
                className="table-section"
                style={{
                  overflowX: 'auto',
                  border: '1px solid #b8d4f0',
                }}>
                <DataTable
                  rows={group.records.map(r => ({
                    id: r.id,
                    ...r.values,
                    _match: r.match_type,
                  }))}
                  headers={headers}>
                  {({ rows, headers, getTableProps, getHeaderProps }) => (
                    <Table
                      {...getTableProps()}
                      stickyHeader
                      style={{
                        minWidth: `${headers.length * 180}px`,
                      }}>
                      <TableHead>
                        <TableRow>
                          {headers.map(h => (
                            <TableHeader
                              key={h.key}
                              {...getHeaderProps({ header: h })}>
                              {h.header}
                            </TableHeader>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {rows.map(row => (
                          <TableRow key={row.id}>
                            {row.cells.map(cell => {
                              const isNicknameColumn =
                                cell.info.header === 'nicknames';
                              return (
                                <TableCell
                                  key={cell.id}
                                  style={
                                    isNicknameColumn ? NICKNAME_CELL_STYLE : {}
                                  }
                                  // style={match ? MATCH_STYLE[match] : {}}
                                >
                                  {cell.value}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </DataTable>
              </div>

              {/* ---------------- Pagination ---------------- */}
              <div className="pagination-section">
                <Pagination
                  totalItems={group.record_count}
                  page={page}
                  pageSize={pageSize}
                  pageSizes={[10, 20, 30]}
                  onChange={({ page, pageSize }) => {
                    setPage(page);
                    setPageSize(pageSize);
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default MatchReviewGroupDetail;
